import React, {ReactElement, useContext, useEffect} from 'react';
import {Button} from '@customink/pigment-react';
import {selectPrimaryCampaign} from '../../slices/storesSlice';
import {
  useLazyGetOrderSummaryQuery,
  useGetStoreDetailsQuery,
  useGetOrderSummaryQuery,
  useLazyGetBuyupQuery
} from '../../api/storesApi';
import {useFetchUserQuery} from '../../api/userApi';
import {useAppDispatch, useAppSelector, useCampaignStatus} from '../../hooks';
import {selectAdmin} from '../../slices/userSlice';
import GroupShareDialog from '../GroupShareDialog';
import services from '../../api/remoteServices';
import {MobileContext} from '../../containers/MobileProvider';
import GroupOrderStatusDialog from './Dialog';
import './index.scss';
import {showGroupOrderStatusDialog} from '../../slices/appSlice';

type GroupStatusBarProps = {
  storeSlug: string;
};

const GroupStatusBar: React.FC<GroupStatusBarProps> = ({
  storeSlug
}: GroupStatusBarProps): ReactElement => {
  const dispatch = useAppDispatch();
  const {refetch: refetchStoreDetails} = useGetStoreDetailsQuery(storeSlug);
  useFetchUserQuery();
  const storeDetails = useAppSelector(state => state.stores.storeDetails);
  const primaryCampaign = useAppSelector(selectPrimaryCampaign);
  const campaignId = primaryCampaign?.id;
  const admin = useAppSelector(selectAdmin);
  const {data: campaignOrderData} = useGetOrderSummaryQuery({
    urlSlug: storeSlug,
    campaignId
  });
  const {isCampaignCompleted, isCampaignGoalMet, isCampaignCanceled} =
    useCampaignStatus();
  const mobile = useContext(MobileContext);
  const showDialog = useAppSelector(state => {
    return state.app.showGroupOrderStatusDialog;
  });

  const [triggerGetOrderSummary, {isFetching: isOrderFetching}] =
    useLazyGetOrderSummaryQuery();

  const [triggerGetBuyup, {data: ccoUrl}] = useLazyGetBuyupQuery();

  useEffect(() => {
    if (storeSlug && campaignId) {
      triggerGetOrderSummary({
        urlSlug: storeSlug,
        campaignId
      });

      triggerGetBuyup({
        urlSlug: storeSlug,
        campaignId
      });
    }
  }, [storeSlug, campaignId]);

  const isBuyupFlagEnabled = campaignOrderData?.buyupEnabled;
  const isGoReadyToComplete =
    !isCampaignCanceled &&
    (isBuyupFlagEnabled || admin || isCampaignCompleted || isCampaignGoalMet);

  const enableButton = primaryCampaign && isGoReadyToComplete;

  const buttonTextForStatus = isCampaignCompleted
    ? 'View Order Summary'
    : 'Complete Group Order';

  const groupStatusBarClasses = `groupStatusBar${mobile ? ' mobile' : ''}`;

  const handleSubmitClick = () => {
    refetchStoreDetails();
    dispatch(showGroupOrderStatusDialog(true));
  };

  return storeDetails ? (
    <div className={groupStatusBarClasses}>
      <GroupShareDialog />
      <Button
        className="groupStatusBar-button"
        variant="secondary"
        size="small"
        href={`${services.storefront}/${storeSlug}`}
        target="_blank"
        rel="noreferrer">
        View Group Order Page
      </Button>
      <Button
        className="groupStatusBar-button buttonLink"
        disabled={!enableButton || isOrderFetching}
        size="small"
        onClick={handleSubmitClick}
        target="_blank"
        rel="noreferrer"
        role="link">
        {buttonTextForStatus}
      </Button>

      {showDialog && (
        <GroupOrderStatusDialog
          storeSlug={storeSlug}
          campaignId={campaignId}
          isMobile={mobile}
          ccoUrl={ccoUrl}
        />
      )}
    </div>
  ) : null;
};

export default GroupStatusBar;
